import { useEffect, useState } from "react";
import client from "../lib/api";
import { money } from "../lib/helpers";
import { FlaskConical, CreditCard, RotateCcw } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";

const STATUS_BADGE = {
  paid: { label: "Paid", cls: "bg-green-100 text-green-800 border-green-200" },
  pending: { label: "Pending", cls: "bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]" },
  refunded: { label: "Refunded", cls: "bg-[#4A0000]/10 text-[#4A0000] border-[#4A0000]/30" },
};

export default function TestPaymentTools({ isAdmin }) {
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("1.00");
  const [busy, setBusy] = useState(false);
  const [refunding, setRefunding] = useState(null);

  const load = () => client.get("/payments/tests").then((r) => setTests(r.data.tests || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const runTest = async () => {
    const amt = Math.round((parseFloat(amount) || 0) * 100) / 100;
    if (amt < 0.5) return toast.error("Test amount must be at least $0.50");
    if (amt > 100) return toast.error("Test amount can't exceed $100");
    setBusy(true);
    try {
      const r = await client.post("/payments/test-checkout", { amount: amt, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start test payment");
      setBusy(false);
    }
  };

  const refund = async (sid) => {
    setRefunding(sid);
    try {
      await client.post("/payments/refund", { session_id: sid });
      toast.success("Test payment refunded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refund");
    } finally { setRefunding(null); }
  };

  return (
    <div className="border border-[#E9DDC6] rounded-md bg-white p-5" data-testid="test-payment-tools">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className="text-[#8a6d0a]" />
          <div>
            <p className="font-display text-lg leading-tight">Test payments</p>
            <p className="text-xs text-[#5C5A58]">Run a small charge to verify checkout. These never count toward real revenue.</p>
          </div>
        </div>
        <Button data-testid="run-test-payment-btn" onClick={() => { setAmount("1.00"); setOpen(true); }}
          className="bg-[#B08D57] hover:bg-[#9a7645] text-white"><FlaskConical size={16} className="mr-1.5" />Run test payment</Button>
      </div>

      {tests.length > 0 && (
        <div className="mt-4 border-t border-[#F0ECE2] pt-3 space-y-2" data-testid="test-payment-list">
          {tests.slice(0, 8).map((t) => {
            const b = STATUS_BADGE[t.status] || STATUS_BADGE.pending;
            return (
              <div key={t.session_id} className="flex items-center justify-between text-sm gap-3" data-testid={`test-row-${t.session_id}`}>
                <span className="font-mono-num font-medium">{money(t.amount)}</span>
                {isAdmin && <span className="text-[#5C5A58] text-xs truncate flex-1">{t.payer_email}</span>}
                <Badge className={b.cls}>{b.label}</Badge>
                {isAdmin && t.status === "paid" && (
                  <Button data-testid={`refund-test-${t.session_id}`} size="sm" variant="outline"
                    disabled={refunding === t.session_id} onClick={() => refund(t.session_id)}
                    className="border-[#E9DDC6] text-[#4A0000] h-7 px-2">
                    <RotateCcw size={13} className="mr-1" />{refunding === t.session_id ? "…" : "Refund"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="test-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Run a test payment</DialogTitle>
            <DialogDescription>Charge a small amount through Stripe checkout to confirm everything works. Refund it anytime.</DialogDescription>
          </DialogHeader>
          <div>
            <p className="text-sm text-[#5C5A58] mb-1">Amount ($)</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
              <Input data-testid="test-payment-amount" type="number" min="0.5" step="0.5" value={amount}
                onChange={(e) => setAmount(e.target.value)} className="pl-7" />
            </div>
            <p className="text-xs text-[#9A968E] mt-1">Test card: 4242 4242 4242 4242 · any future expiry · any CVC.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="test-payment-cancel" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="test-payment-confirm" onClick={runTest} disabled={busy} className="bg-[#8a6d0a] hover:bg-[#6f5708] text-white">
              <CreditCard size={16} className="mr-1.5" />{busy ? "Redirecting…" : "Continue to Stripe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
